/* iter-p2-z — Admin Email Translator.
 *
 * Paste any customer-support text, pick a target language, get the
 * translated body back for pasting into Gmail/Zendesk. Uses the
 * `/api/admin/emails/translator/*` endpoints which are gated behind
 * require_admin.
 *
 * Reachable from the Admin Hub tile.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Copy, Loader2, Languages, Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { adminAuth, adminEmailTranslatorApi } from '@/lib/api';

const KIND_OPTIONS = [
  { id: 'generic',   label: 'Generic email' },
  { id: 'reply',     label: 'Customer reply' },
  { id: 'info',      label: 'Informational' },
  { id: 'complaint', label: 'Complaint response' },
  { id: 'request',   label: 'Request to customer' },
];

// Focus first on the four languages the QA batch asked for; everything
// else the backend supports is still selectable via the dropdown.
const PRIMARY_CODES = ['en', 'es', 'ar', 'zh'];

export default function AdminEmailTranslator() {
  const [languages, setLanguages] = useState([]);
  const [target, setTarget]       = useState('es');
  const [source, setSource]       = useState('en');
  const [kind, setKind]           = useState('reply');
  const [text, setText]           = useState('');
  const [output, setOutput]       = useState('');
  const [busy, setBusy]           = useState(false);

  useEffect(() => {
    let alive = true;
    adminEmailTranslatorApi.languages()
      .then((r) => { if (alive) setLanguages(r.languages || []); })
      .catch(() => { /* stays with the empty list — Select still usable */ });
    return () => { alive = false; };
  }, []);

  const sortedLanguages = useMemo(() => {
    const primary = languages.filter((l) => PRIMARY_CODES.includes(l.code));
    const rest    = languages.filter((l) => !PRIMARY_CODES.includes(l.code));
    return [...primary, ...rest];
  }, [languages]);

  // Auth guard — /admin/* requires a live admin session cookie.
  // Placed AFTER all hooks to obey rules-of-hooks.
  if (typeof window !== 'undefined' && !adminAuth.user) {
    return <Navigate to="/admin" replace />;
  }

  const targetLabel = sortedLanguages.find((l) => l.code === target)?.label || target;

  const translate = async () => {
    if (!text.trim()) { toast.error('Paste some text first.'); return; }
    setBusy(true);
    setOutput('');
    try {
      const r = await adminEmailTranslatorApi.translate({
        text, target_language: target, source_language: source, kind,
      });
      setOutput(r?.translated_text || '');
      toast.success(`Translated to ${r?.target_label || targetLabel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translation failed.');
    } finally { setBusy(false); }
  };

  const copyOut = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      toast.success('Copied to clipboard');
    } catch { toast.error('Could not copy — highlight and Ctrl+C instead.'); }
  };

  const isRTLTarget = target === 'ar' || target === 'he';

  return (
    <div className="bg-background text-foreground" data-testid="admin-email-translator-page">
      <header className="flex items-center justify-between border-b border-border bg-panel-header/40 px-4 py-2">
        <div className="flex items-center gap-2">
          <Link to="/admin">
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]">
              <ArrowLeft className="h-3.5 w-3.5" /> Admin Hub
            </Button>
          </Link>
          <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-primary-foreground"><Languages className="h-3.5 w-3.5" /></div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin · Email Support</div>
            <div className="text-[13px] font-semibold leading-tight">Email Translator</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <Card className="p-4">
          <p className="text-[12.5px] text-muted-foreground">
            Paste an inbound message or your English draft, pick the
            target language, and get a clean translation you can paste
            straight into your support tool. Powered by Claude Sonnet
            4.5 via the Emergent Universal Key.
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div>
              <Label className="text-[11.5px]">Kind of email</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="mt-1 h-8 text-[12.5px]" data-testid="translator-kind-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((k) => (
                    <SelectItem key={k.id} value={k.id} data-testid={`translator-kind-option-${k.id}`}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11.5px]">Source language (optional)</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="mt-1 h-8 text-[12.5px]" data-testid="translator-source-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortedLanguages.map((l) => (
                    <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11.5px]">Target language</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1 h-8 text-[12.5px]" data-testid="translator-target-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortedLanguages.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`translator-target-option-${l.code}`}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-[11.5px]">Source text</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste the email here…"
                className="mt-1 min-h-[240px] font-mono-stat text-[12px]"
                data-testid="translator-source-text"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[11.5px]">Translated ({targetLabel})</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyOut}
                  disabled={!output}
                  className="h-7 gap-1.5 text-[11.5px]"
                  data-testid="translator-copy"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <Textarea
                value={output}
                readOnly
                placeholder={busy ? 'Translating…' : 'Translation will appear here.'}
                className={`mt-1 min-h-[240px] text-[13px] ${isRTLTarget ? 'lang-rtl' : ''}`}
                dir={isRTLTarget ? 'rtl' : undefined}
                data-testid="translator-output"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              onClick={translate}
              disabled={busy || !text.trim()}
              className="gap-1.5"
              data-testid="translator-run"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              Translate
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
}
